import React, { useState } from 'react'
import { TrendingUp, TrendingDown, DollarSign, Users, Lock, Zap, ArrowUpRight, ArrowDownRight, Activity, Shield, BarChart3 } from 'lucide-react'

interface DeFiProtocolProps {
  data: {
    protocolName: string
    description: string
    totalValueLocked: string
    apy: string
    apyChange: number
    totalUsers: number
    totalPools: number
    tokenPrice: string
    tokenPriceChange: number
    pools: Array<{
      id: string
      name: string
      tokens: string[]
      apy: string
      tvl: string
      volume24h: string
      fees24h: string
      apyChange: number
    }>
    stats: {
      totalVolume: string
      totalFees: string
      activeUsers: number
      transactions24h: number
    }
    features: Array<{
      title: string
      description: string
      icon: string
    }>
    security: {
      audits: number
      bugBounties: boolean
      insurance: boolean
      timelock: boolean
    }
  }
}

export default function DeFiProtocol({ data }: DeFiProtocolProps) {
  const [selectedPool, setSelectedPool] = useState<string | null>(null)
  const [timeframe, setTimeframe] = useState<'24h' | '7d' | '30d'>('24h')

  const formatNumber = (num: string) => {
    const value = parseFloat(num.replace(/[,$]/g, ''))
    if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`
    if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`
    if (value >= 1e3) return `$${(value / 1e3).toFixed(2)}K`
    return `$${value.toFixed(2)}`
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-cyan-900 text-white">
      {/* Header */}
      <header className="bg-black/50 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                {data.protocolName}
              </div>
            </div>
            <nav className="hidden md:flex space-x-8">
              <a href="#pools" className="text-white/80 hover:text-white transition-colors">Pools</a>
              <a href="#stats" className="text-white/80 hover:text-white transition-colors">Analytics</a>
              <a href="#security" className="text-white/80 hover:text-white transition-colors">Security</a>
              <a href="#docs" className="text-white/80 hover:text-white transition-colors">Docs</a>
            </nav>
            <div className="flex items-center space-x-4">
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors">
                Connect Wallet
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h1 
            className="text-5xl md:text-7xl font-bold mb-6"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            {data.protocolName}
          </h1>
          <p className="text-xl md:text-2xl text-blue-200 mb-12 max-w-3xl mx-auto">
            {data.description}
          </p>
          
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div className="bg-gradient-to-r from-blue-500/20 to-blue-600/20 backdrop-blur-sm rounded-2xl p-6 border border-blue-400/30">
              <div className="flex items-center justify-between mb-2">
                <span className="text-blue-400 font-semibold">Total Value Locked</span>
                <Lock className="w-5 h-5 text-blue-400" />
              </div>
              <div className="text-3xl font-bold text-white">{data.totalValueLocked}</div>
            </div>
            
            <div className="bg-gradient-to-r from-green-500/20 to-green-600/20 backdrop-blur-sm rounded-2xl p-6 border border-green-400/30">
              <div className="flex items-center justify-between mb-2">
                <span className="text-green-400 font-semibold">Average APY</span>
                <TrendingUp className="w-5 h-5 text-green-400" />
              </div>
              <div className="flex items-center gap-2">
                <div className="text-3xl font-bold text-white">{data.apy}</div>
                <div className={`flex items-center text-sm ${
                  data.apyChange >= 0 ? 'text-green-400' : 'text-red-400'
                }`}>
                  {data.apyChange >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                  {Math.abs(data.apyChange)}%
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-purple-500/20 to-purple-600/20 backdrop-blur-sm rounded-2xl p-6 border border-purple-400/30">
              <div className="flex items-center justify-between mb-2">
                <span className="text-purple-400 font-semibold">Total Users</span>
                <Users className="w-5 h-5 text-purple-400" />
              </div>
              <div className="text-3xl font-bold text-white">{data.totalUsers.toLocaleString()}</div>
            </div>
            
            <div className="bg-gradient-to-r from-cyan-500/20 to-cyan-600/20 backdrop-blur-sm rounded-2xl p-6 border border-cyan-400/30">
              <div className="flex items-center justify-between mb-2">
                <span className="text-cyan-400 font-semibold">Active Pools</span>
                <Activity className="w-5 h-5 text-cyan-400" />
              </div>
              <div className="text-3xl font-bold text-white">{data.totalPools}</div>
            </div>
          </div>

          {/* Token Price */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 max-w-md mx-auto">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/80 font-semibold">Token Price</span>
              <DollarSign className="w-5 h-5 text-white/80" />
            </div>
            <div className="flex items-center gap-2">
              <div className="text-2xl font-bold text-white">{data.tokenPrice}</div>
              <div className={`flex items-center text-sm ${
                data.tokenPriceChange >= 0 ? 'text-green-400' : 'text-red-400'
              }`}>
                {data.tokenPriceChange >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                {Math.abs(data.tokenPriceChange)}%
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pools Section */}
      <section className="py-16 px-6 bg-black/20">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h2 
              className="text-3xl font-bold"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              Liquidity Pools
            </h2>
            
            <select
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value as any)}
              className="bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-blue-400"
            >
              <option value="24h">24 Hours</option>
              <option value="7d">7 Days</option>
              <option value="30d">30 Days</option>
            </select>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.pools.map((pool) => (
              <div
                key={pool.id}
                className={`bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:border-blue-400/50 transition-all duration-300 cursor-pointer ${
                  selectedPool === pool.id ? 'ring-2 ring-blue-400' : ''
                }`}
                onClick={() => setSelectedPool(selectedPool === pool.id ? null : pool.id)}
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white">{pool.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      {pool.tokens.map((token, index) => (
                        <span key={index} className="text-sm text-blue-300">{token}</span>
                      ))}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-green-400">{pool.apy}</div>
                    <div className={`flex items-center text-sm ${
                      pool.apyChange >= 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {pool.apyChange >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                      {Math.abs(pool.apyChange)}%
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-white/70">TVL</span>
                    <span className="text-white font-medium">{pool.tvl}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/70">Volume 24h</span>
                    <span className="text-white font-medium">{pool.volume24h}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/70">Fees 24h</span>
                    <span className="text-white font-medium">{pool.fees24h}</span>
                  </div>
                </div>
                
                <button className="w-full mt-4 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-semibold transition-colors">
                  Provide Liquidity
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Protocol Stats */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 
            className="text-3xl font-bold mb-8 text-center"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Protocol Analytics
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-r from-blue-500/20 to-blue-600/20 backdrop-blur-sm rounded-xl p-6 border border-blue-400/30">
              <div className="flex items-center gap-3 mb-2">
                <BarChart3 className="w-6 h-6 text-blue-400" />
                <span className="text-blue-400 font-semibold">Total Volume</span>
              </div>
              <div className="text-2xl font-bold text-white">{data.stats.totalVolume}</div>
            </div>
            
            <div className="bg-gradient-to-r from-green-500/20 to-green-600/20 backdrop-blur-sm rounded-xl p-6 border border-green-400/30">
              <div className="flex items-center gap-3 mb-2">
                <DollarSign className="w-6 h-6 text-green-400" />
                <span className="text-green-400 font-semibold">Total Fees</span>
              </div>
              <div className="text-2xl font-bold text-white">{data.stats.totalFees}</div>
            </div>
            
            <div className="bg-gradient-to-r from-purple-500/20 to-purple-600/20 backdrop-blur-sm rounded-xl p-6 border border-purple-400/30">
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-6 h-6 text-purple-400" />
                <span className="text-purple-400 font-semibold">Active Users</span>
              </div>
              <div className="text-2xl font-bold text-white">{data.stats.activeUsers.toLocaleString()}</div>
            </div>
            
            <div className="bg-gradient-to-r from-cyan-500/20 to-cyan-600/20 backdrop-blur-sm rounded-xl p-6 border border-cyan-400/30">
              <div className="flex items-center gap-3 mb-2">
                <Activity className="w-6 h-6 text-cyan-400" />
                <span className="text-cyan-400 font-semibold">Transactions 24h</span>
              </div>
              <div className="text-2xl font-bold text-white">{data.stats.transactions24h.toLocaleString()}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-6 bg-black/20">
        <div className="max-w-6xl mx-auto">
          <h2 
            className="text-3xl font-bold mb-12 text-center"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Protocol Features
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {data.features.map((feature, index) => (
              <div
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
              >
                <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-4">
                  <Zap className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-white/70">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 
            className="text-3xl font-bold mb-12 text-center"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Security & Audits
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-r from-green-500/20 to-green-600/20 backdrop-blur-sm rounded-xl p-6 border border-green-400/30 text-center">
              <Shield className="w-8 h-8 text-green-400 mx-auto mb-3" />
              <div className="text-2xl font-bold text-white mb-1">{data.security.audits}</div>
              <div className="text-green-400 font-semibold">Security Audits</div>
            </div>
            
            <div className="bg-gradient-to-r from-blue-500/20 to-blue-600/20 backdrop-blur-sm rounded-xl p-6 border border-blue-400/30 text-center">
              <Shield className="w-8 h-8 text-blue-400 mx-auto mb-3" />
              <div className="text-2xl font-bold text-white mb-1">
                {data.security.bugBounties ? '✓' : '✗'}
              </div>
              <div className="text-blue-400 font-semibold">Bug Bounties</div>
            </div>
            
            <div className="bg-gradient-to-r from-purple-500/20 to-purple-600/20 backdrop-blur-sm rounded-xl p-6 border border-purple-400/30 text-center">
              <Shield className="w-8 h-8 text-purple-400 mx-auto mb-3" />
              <div className="text-2xl font-bold text-white mb-1">
                {data.security.insurance ? '✓' : '✗'}
              </div>
              <div className="text-purple-400 font-semibold">Insurance</div>
            </div>
            
            <div className="bg-gradient-to-r from-cyan-500/20 to-cyan-600/20 backdrop-blur-sm rounded-xl p-6 border border-cyan-400/30 text-center">
              <Lock className="w-8 h-8 text-cyan-400 mx-auto mb-3" />
              <div className="text-2xl font-bold text-white mb-1">
                {data.security.timelock ? '✓' : '✗'}
              </div>
              <div className="text-cyan-400 font-semibold">Timelock</div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black/50 backdrop-blur-sm border-t border-white/10 py-12 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex justify-center gap-6 mb-6">
            <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors">Twitter</a>
            <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors">Discord</a>
            <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors">GitHub</a>
            <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors">Docs</a>
          </div>
          <p className="text-gray-400">
            &copy; 2024 {data.protocolName}. Decentralized Finance Protocol.
          </p>
        </div>
      </footer>
    </div>
  )
}
